import React, { useState, Component } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { ArrowRight, Building2, Briefcase, PenTool, Globe, CheckCircle2, Mail, MapPin, Zap, BarChart3, Layers } from 'lucide-react';
export function LandingPage() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Construct mailto link
    const subject = `New Message from ${formState.name}`;
    const body = `Name: ${formState.name}%0D%0AEmail: ${formState.email}%0D%0A%0D%0AMessage:%0D%0A${formState.message}`;
    const mailtoLink = `mailto:hello@hbr-company.com?subject=${encodeURIComponent(subject)}&body=${body}`; // Note: body is already partially encoded with %0D%0A but let's be safe with user content if we were using encodeURIComponent fully, but here simple concatenation for the body structure is often used. Better to use encodeURIComponent for the variable parts.
    // Let's do it properly:
    const safeSubject = encodeURIComponent(`New Message from ${formState.name}`);
    const safeBody = encodeURIComponent(`Name: ${formState.name}\nEmail: ${formState.email}\n\nMessage:\n${formState.message}`);
    window.location.href = `mailto:hello@hbr-company.com?subject=${safeSubject}&body=${safeBody}`;
    // Reset UI state
    setIsSubmitting(false);
    setSubmitStatus('success');
    setFormState({
      name: '',
      email: '',
      message: ''
    });
  };
  return <Layout data-id="element-70">
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white py-24 md:py-32 overflow-hidden" data-id="element-71">
        {/* Abstract Background Pattern */}
        <div className="absolute inset-0 opacity-10 pointer-events-none" data-id="element-72">
          <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-73">
            <path d="M0 100 L100 0 L100 100 Z" fill="currentColor" data-id="element-74" />
          </svg>
        </div>
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-slate-800 to-transparent opacity-30 pointer-events-none" data-id="element-75" />

        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-76">
          <div className="max-w-4xl" data-id="element-77">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 text-sm font-medium mb-6" data-id="element-78">
              <span className="relative flex h-2 w-2" data-id="element-79">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" data-id="element-80"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" data-id="element-81"></span>
              </span>
              Accepting New Clients for 2026
            </div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 leading-tight" data-id="element-82">
              Built for infrastructure. <br data-id="element-83" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-amber-500" data-id="element-84">
                Built for business.
              </span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl leading-relaxed" data-id="element-85">
              HBR Company combines decades of infrastructure expertise and
              modern storytelling strategy. We help businesses build better
              foundations and tell better stories.
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-86">
              <Button variant="secondary" size="lg" onClick={() => document.getElementById('contact')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-87">
                Start a Conversation
              </Button>
              <Button variant="outline" size="lg" className="text-white border-slate-600 hover:bg-slate-800 hover:text-white" onClick={() => document.getElementById('work')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-88">
                View Our Work
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* #1 — Services Section */}
      <section id="services" className="py-20 bg-white" data-id="element-89">
        <div className="container mx-auto px-4 md:px-6" data-id="element-90">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12" data-id="element-91">
            <div className="max-w-2xl" data-id="element-92">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-93">
                What We Do
              </h2>
              <p className="text-lg text-slate-600" data-id="element-94">
                Comprehensive consulting services bridging the gap between
                physical infrastructure and digital strategy.
              </p>
            </div>
            <a href="https://mindfulmondays.travishellstrom.com/HBR-Company-Capabilities-2026.pdf" target="_blank" rel="noopener noreferrer" className="hidden md:flex" data-id="element-95">
              <Button variant="outline" className="mt-4 md:mt-0" data-id="element-96">
                Download Capabilities Deck
              </Button>
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-97">
            <Card hover className="p-6 border-t-4 border-t-amber-500" data-id="element-98">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center text-amber-600 mb-4" data-id="element-99">
                <Building2 size={24} data-id="element-100" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-101">
                Construction & Infrastructure
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed" data-id="element-102">
                Strategic consulting for large-scale construction and utility
                projects. We optimize workflows and ensure operational
                excellence.
              </p>
            </Card>

            <Card hover className="p-6 border-t-4 border-t-slate-900" data-id="element-103">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center text-slate-900 mb-4" data-id="element-104">
                <Briefcase size={24} data-id="element-105" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-106">
                Project Management
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed" data-id="element-107">
                PMP-certified management methodologies. From initiation to
                closing, we ensure your projects are delivered on time and
                within budget.
              </p>
            </Card>

            <Card hover className="p-6 border-t-4 border-t-amber-500" data-id="element-108">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center text-amber-600 mb-4" data-id="element-109">
                <PenTool size={24} data-id="element-110" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-111">
                Strategy & Content
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed" data-id="element-112">
                Brand storytelling and business strategy. We craft compelling
                narratives that resonate with stakeholders and drive growth.
              </p>
            </Card>

            <Card hover className="p-6 border-t-4 border-t-slate-900" data-id="element-113">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center text-slate-900 mb-4" data-id="element-114">
                <Globe size={24} data-id="element-115" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-116">
                Web & Digital Presence
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed" data-id="element-117">
                Modern digital solutions for traditional industries. We build
                professional web presences that reflect your operational
                quality.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* #2 — Our Ecosystem */}
      <section id="work" className="py-20 bg-slate-50 border-y border-stone-200" data-id="element-118">
        <div className="container mx-auto px-4 md:px-6" data-id="element-119">
          <div className="text-center mb-16" data-id="element-120">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-121">
              Our Ecosystem
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-122">
              A portfolio of expertise spanning infrastructure, operations,
              storytelling, and digital strategy.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-123">
            <Card className="p-6 bg-white border-stone-200" data-id="element-124">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center text-amber-600 mb-4" data-id="element-125">
                <Building2 size={24} data-id="element-126" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-127">
                Infrastructure Development
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed" data-id="element-128">
                End-to-end advisory for large-scale civil, utility, and
                construction projects. From site planning through commissioning,
                we bring structure and accountability to complex builds.
              </p>
            </Card>

            <Card className="p-6 bg-white border-stone-200" data-id="element-129">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center text-slate-700 mb-4" data-id="element-130">
                <Briefcase size={24} data-id="element-131" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-132">
                Project Delivery
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed" data-id="element-133">
                PMP-certified project management across enterprise and
                mid-market clients. We define scope, manage risk, and drive
                delivery — on time and within budget, every time.
              </p>
            </Card>

            <Card className="p-6 bg-white border-stone-200" data-id="element-134">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center text-amber-600 mb-4" data-id="element-135">
                <Zap size={24} data-id="element-136" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-137">
                Operational Excellence
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed" data-id="element-138">
                Lean Six Sigma-driven process improvement for organizations that
                demand efficiency. We identify waste, streamline workflows, and
                build systems that scale with your growth.
              </p>
            </Card>

            <Card className="p-6 bg-white border-stone-200" data-id="element-139">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center text-slate-700 mb-4" data-id="element-140">
                <PenTool size={24} data-id="element-141" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-142">
                Brand & Content Strategy
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed" data-id="element-143">
                Compelling narratives rooted in research and historical insight.
                From executive thought leadership to full brand voice
                development, we help organizations articulate what they stand
                for.
              </p>
            </Card>

            <Card className="p-6 bg-white border-stone-200" data-id="element-144">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center text-amber-600 mb-4" data-id="element-145">
                <Globe size={24} data-id="element-146" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-147">
                Digital Transformation
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed" data-id="element-148">
                Modern web and digital infrastructure for traditional
                industries. We design and build professional digital presences
                that match the quality and credibility of your physical
                operations.
              </p>
            </Card>

            <Card className="p-6 bg-white border-stone-200" data-id="element-149">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center text-slate-700 mb-4" data-id="element-150">
                <BarChart3 size={24} data-id="element-151" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-152">
                Enterprise Advisory
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed" data-id="element-153">
                Strategic counsel for enterprise-scale organizations navigating
                growth, transformation, or operational complexity. Informed by
                real-world experience at companies like Southern Company.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* #3 — Meet the Brothers */}
      <section id="about" className="py-20 bg-stone-50" data-id="element-154">
        <div className="container mx-auto px-4 md:px-6" data-id="element-155">
          <div className="text-center mb-16" data-id="element-156">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-157">
              Meet the Brothers
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-158">
              Two distinct paths, one shared vision. We bring together the
              analytical and the creative to solve complex business problems.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-159">
            {/* Travis */}
            <Card className="flex flex-col md:flex-row overflow-hidden bg-white border-stone-200" data-id="element-160">
              <div className="md:w-2/5 bg-slate-200 relative min-h-[300px] md:min-h-full" data-id="element-161">
                <img src="https://cdn.magicpatterns.com/uploads/oAcCho7YLRx8CKvBnbnNm9/Travis_Hellstrom_Heashot.jpg" alt="Travis Hellstrom" className="absolute inset-0 w-full h-full object-cover object-[50%_20%]" data-id="element-162" />
              </div>
              <div className="p-8 md:w-3/5 flex flex-col justify-center" data-id="element-163">
                <h3 className="text-2xl font-bold text-slate-900 mb-1" data-id="element-164">
                  Travis Hellstrom
                </h3>
                <p className="text-amber-600 font-medium mb-4" data-id="element-165">
                  Author & Entrepreneur
                </p>
                <p className="text-slate-600 mb-4 text-sm leading-relaxed" data-id="element-166">
                  Bestselling author writing on historical leaders like Lincoln,
                  Franklin, and Gandhi. Expert in content strategy, media, and
                  storytelling. Travis helps brands find their voice and
                  articulate their vision with clarity and impact.
                </p>
                <div className="mt-auto" data-id="element-167">
                  <a href="https://travishellstrom.com" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-slate-900 hover:text-amber-600 inline-flex items-center gap-1 transition-colors" data-id="element-168">
                    travishellstrom.com <ArrowRight size={14} data-id="element-169" />
                  </a>
                </div>
              </div>
            </Card>

            {/* Elias */}
            <Card className="flex flex-col md:flex-row overflow-hidden bg-white border-stone-200" data-id="element-170">
              <div className="md:w-2/5 bg-slate-200 relative min-h-[300px] md:min-h-full" data-id="element-171">
                <img src="https://cdn.magicpatterns.com/uploads/mGDaVwNBX4yyLWxDSAX4f8/1702522729696.jpg" alt="Elias Hellstrom" className="absolute inset-0 w-full h-full object-cover object-[50%_20%]" data-id="element-172" />
              </div>
              <div className="p-8 md:w-3/5 flex flex-col justify-center" data-id="element-173">
                <h3 className="text-2xl font-bold text-slate-900 mb-1" data-id="element-174">
                  Elias Hellstrom
                </h3>
                <p className="text-amber-600 font-medium mb-4" data-id="element-175">
                  Project Manager, PMP
                </p>
                <p className="text-slate-600 mb-4 text-sm leading-relaxed" data-id="element-176">
                  Certified PMP and Lean Six Sigma professional with deep
                  experience in construction, utilities, and enterprise
                  operations. Recently at Southern Company. Elias brings
                  rigorous structure and efficiency to complex infrastructure
                  projects.
                </p>
                <div className="mt-auto" data-id="element-177">
                  <a href="https://eliashellstrom.com" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-slate-900 hover:text-amber-600 inline-flex items-center gap-1 transition-colors" data-id="element-178">
                    eliashellstrom.com <ArrowRight size={14} data-id="element-179" />
                  </a>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white" data-id="element-180">
        <div className="container mx-auto px-4 md:px-6" data-id="element-181">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-182">
            <div data-id="element-183">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-184">
                Let's Build Something Together
              </h2>
              <p className="text-lg text-slate-600 mb-8" data-id="element-185">
                Whether you need infrastructure consulting or a new content
                strategy, we're ready to help. Reach out to start the
                conversation.
              </p>

              <div className="space-y-6" data-id="element-186">
                <div className="flex items-start gap-4" data-id="element-187">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 flex-shrink-0" data-id="element-188">
                    <Mail size={20} data-id="element-189" />
                  </div>
                  <div data-id="element-190">
                    <h3 className="font-semibold text-slate-900" data-id="element-191">Email Us</h3>
                    <p className="text-slate-600" data-id="element-192">hello@hbr-company.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-193">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 flex-shrink-0" data-id="element-194">
                    <MapPin size={20} data-id="element-195" />
                  </div>
                  <div data-id="element-196">
                    <h3 className="font-semibold text-slate-900" data-id="element-197">Office</h3>
                    <p className="text-slate-600" data-id="element-198">
                      Based in the USA
                      <br data-id="element-199" />
                      Available Globally
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Card className="p-8 shadow-lg border-stone-200" data-id="element-200">
              {submitStatus === 'success' ? <div className="text-center py-12" data-id="element-201">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-202">
                    <CheckCircle2 size={32} data-id="element-203" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-204">
                    Opening Email Client...
                  </h3>
                  <p className="text-slate-600 mb-6" data-id="element-205">
                    We've prepared an email for you. If it didn't open
                    automatically, please click below.
                  </p>
                  <Button onClick={() => {
                const safeSubject = encodeURIComponent(`New Message from ${formState.name}`);
                const safeBody = encodeURIComponent(`Name: ${formState.name}\nEmail: ${formState.email}\n\nMessage:\n${formState.message}`);
                window.location.href = `mailto:hello@hbr-company.com?subject=${safeSubject}&body=${safeBody}`;
              }} variant="outline" data-id="element-206">
                    Open Email Client
                  </Button>
                  <div className="mt-4" data-id="element-207">
                    <Button onClick={() => setSubmitStatus('idle')} variant="ghost" size="sm" data-id="element-208">
                      Reset Form
                    </Button>
                  </div>
                </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-209">
                  <h3 className="text-xl font-bold text-slate-900 mb-4" data-id="element-210">
                    Send a Message
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-211">
                    <Input label="Name" placeholder="Your name" required value={formState.name} onChange={e => setFormState({
                  ...formState,
                  name: e.target.value
                })} data-id="element-212" />
                    <Input label="Email" type="email" placeholder="you@company.com" required value={formState.email} onChange={e => setFormState({
                  ...formState,
                  email: e.target.value
                })} data-id="element-213" />
                  </div>
                  <Textarea label="Message" placeholder="How can we help you?" rows={4} required value={formState.message} onChange={e => setFormState({
                ...formState,
                message: e.target.value
              })} data-id="element-214" />
                  <Button type="submit" className="w-full" isLoading={isSubmitting} data-id="element-215">
                    Send Message
                  </Button>
                </form>}
            </Card>
          </div>
        </div>
      </section>
    </Layout>;
}